import { isValidVIN } from '@ltvco/refresh-lib/v1';
import {
  fetchReportByPermalinkInLine,
  getVehiclePermalink,
  SEARCH_TYPES,
} from '@ltvco/refresh-lib/v2';
import { VehicleReport } from '@ltvco/refresh-lib/v1';
import type { GenericReportResponse } from '@ltvco/refresh-lib/v2';
import { ERROR_MESSAGES } from './constants';
import { commaFormatCurrency } from '@ltvco/refresh-lib/utils';
import { QuarterlyTrend, VehicleForecast } from '@ltvco/refresh-lib/api';

export interface QuarterlyTrendWithValue extends QuarterlyTrend {
  value: number;
}

export interface VehicleForecastWithValues extends Omit<
  VehicleForecast,
  'trend_by_quarter'
> {
  trend_by_quarter: QuarterlyTrendWithValue[];
}

export interface VehicleForecastData {
  year?: number;
  make?: string;
  model?: string;
  trim?: string;
  incidents: number;
  owners: number;
  recalls: number;
  thefts: number;
  titleIssues: number;
  marketValue: number;
  mileage?: number;
}

export interface VehicleValuationParams {
  mileage?: number;
  region?: string;
  vin?: string;
  plate?: string;
  state?: string;
  year?: string;
  make?: string;
  model?: string;
}

export const isValidVehicleValue = (value: any): boolean => {
  return (
    value &&
    value !== 'Unknown' &&
    value !== 'unknown' &&
    (typeof value === 'string' ? value.trim() !== '' : true)
  );
};

export const extractVehicleSearchParams = (
  vehicleArgs: Record<string, any>,
): {
  vin?: string;
  year?: string;
  make?: string;
  model?: string;
  trim?: string;
  licensePlate?: string;
  state?: string;
} => {
  return {
    ...(isValidVehicleValue(vehicleArgs.vehicle_vin) && {
      vin: vehicleArgs.vehicle_vin,
    }),
    ...(isValidVehicleValue(vehicleArgs.vehicle_year) && {
      year: vehicleArgs.vehicle_year,
    }),
    ...(isValidVehicleValue(vehicleArgs.vehicle_make) && {
      make: vehicleArgs.vehicle_make,
    }),
    ...(isValidVehicleValue(vehicleArgs.vehicle_model) && {
      model: vehicleArgs.vehicle_model,
    }),
    ...(isValidVehicleValue(vehicleArgs.vehicle_trim) && {
      trim: vehicleArgs.vehicle_trim,
    }),
    ...(isValidVehicleValue(vehicleArgs.vehicle_license_plate) && {
      licensePlate: vehicleArgs.vehicle_license_plate,
    }),
    ...(isValidVehicleValue(vehicleArgs.state) && { state: vehicleArgs.state }),
  };
};

export const extractVehicleDataFromReport = (
  reportData: GenericReportResponse,
): VehicleForecastData | null => {
  const vehicleReport = new VehicleReport(reportData as never);
  const processedReportData = vehicleReport.data;
  const currentVehicle = processedReportData.automobiles?.[0];

  if (!currentVehicle) {
    return null;
  }

  const accidentsCount = currentVehicle.accidentsCount || 0;
  const damagesCount =
    currentVehicle.damage_history?.reported_damage?.length || 0;

  return {
    year: currentVehicle.year || undefined,
    make: currentVehicle.make || undefined,
    model: currentVehicle.model || undefined,
    trim: currentVehicle.design?.trim || undefined,
    incidents: accidentsCount + damagesCount,
    owners: currentVehicle.ownership_history?.length || 0,
    recalls: currentVehicle.openRecallsCount || 0,
    thefts: currentVehicle.theftsCount || 0,
    titleIssues: currentVehicle.titleChecksCount || 0,
    marketValue: currentVehicle.valuation?.clean_retail?.adjusted || 0,
    mileage: currentVehicle.mileage?.miles || undefined,
  };
};

export const fetchVehicleReport = async (
  vehicleSearchParams: {
    vin?: string;
    year?: string;
    make?: string;
    model?: string;
    trim?: string;
    licensePlate?: string;
    state?: string;
  },
  isFreePlan: boolean,
): Promise<{
  success: boolean;
  reportData?: GenericReportResponse;
  error?: string;
}> => {
  try {
    const validatedVin =
      vehicleSearchParams.vin && isValidVIN(vehicleSearchParams.vin)
        ? vehicleSearchParams.vin
        : undefined;

    const permalinkResult = await getVehiclePermalink({
      ...vehicleSearchParams,
      vin: validatedVin,
    });

    if (!permalinkResult.permalink) {
      // If it's a 426 error, throw a proper ResponseError for QueryWrapper to handle
      if (permalinkResult.statusCode === 426) {
        const error: any = new Error(
          permalinkResult.error || 'Upgrade Required',
        );
        error.response = {
          status: 426,
          statusText: 'Upgrade Required',
          json: async () => ({
            errors: [],
            pp_version_bad: false,
            tos_version_bad: false,
            meta: { free_user: isFreePlan },
          }),
        };
        error.passthrough = false;
        throw error;
      }

      return {
        success: false,
        error: permalinkResult.error || ERROR_MESSAGES.TECHNICAL_DIFFICULTIES,
      };
    }

    const reportData = (await fetchReportByPermalinkInLine(
      permalinkResult.permalink,
      SEARCH_TYPES.VEHICLE,
    )) as GenericReportResponse;

    return {
      success: true,
      reportData,
    };
  } catch (error: any) {
    throw error;
  }
};

export const formatNumericValue = (value: string): string => {
  const numericValue = value.replace(/\D/g, '');
  return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

// Remove special characters except spaces, keep alphanumeric
export const cleanSearchInput = (value: string): string => {
  return value
    .replace(/[^a-zA-Z0-9\s]/g, '')
    .replace(/\s+/g, ' ')
    .replace(/^\s+/, '');
};

// Remove special characters except spaces, keep periods, commas, apostrophes, hyphens
export const cleanSearchInputWithSpecialCharacters = (
  value: string,
): string => {
  return value
    .replace(/[^a-zA-Z0-9\s.,'\-]/g, '')
    .replace(/\s+/g, ' ')
    .replace(/^\s+/, '');
};

export const formatPriceRange = (min: number, max: number): string => {
  return `${commaFormatCurrency(min)} - ${commaFormatCurrency(max)}`;
};

export const parseNumericInput = (value: string): number => {
  return parseInt(value.replace(/,/g, ''), 10) || 0;
};

// Type for vehicle search result from smart search (fields come as camelCase at runtime)
export type VehicleSearchResult = {
  type: string;
  vin?: string;
  year?: string;
  make?: string;
  model?: string;
  trim?: string;
  licensePlate?: string;
  state?: string;
};

export type VehicleSearchType = 'VIN' | 'YMM' | 'LP' | null;

/**
 * Determine search type based on which vehicle fields are populated
 */
export const determineSearchType = (
  result: VehicleSearchResult,
): VehicleSearchType => {
  const hasValidVin =
    typeof result.vin === 'string' &&
    result.vin.trim() !== '' &&
    result.vin.toLowerCase() !== 'unknown';

  if (hasValidVin) return 'VIN';
  if (result.licensePlate && result.state) return 'LP';
  if (result.year || result.make || result.model) return 'YMM';
  return null;
};

/**
 * Extract vehicle parameters from smart search result for API call
 * Maps camelCase fields to snake_case API parameters
 */
export const extractVehicleParams = (
  result: VehicleSearchResult,
): Record<string, string> => {
  const params: Record<string, string> = {};
  const vin = result.vin;
  if (
    vin &&
    typeof vin === 'string' &&
    vin.trim() !== '' &&
    vin.toLowerCase() !== 'unknown'
  ) {
    params.vehicle_vin = vin;
  }
  if (result.year) params.vehicle_year = result.year;
  if (result.make) params.vehicle_make = result.make;
  if (result.model) params.vehicle_model = result.model;

  // For YMM search (year, make, model without VIN), use 'Base' as default trim if not specified
  const isYMMSearch =
    result.year &&
    result.make &&
    result.model &&
    (!result.vin || result.vin === 'Unknown');

  if (result.trim) {
    params.vehicle_trim = result.trim;
  } else if (isYMMSearch) {
    params.vehicle_trim = 'Base';
  }

  if (result.licensePlate) params.vehicle_license_plate = result.licensePlate;
  if (result.state) params.state = result.state;

  return params;
};

export const parseVehicleParams = (
  vehicleParams: string | Record<string, any> | undefined,
): Record<string, any> | null => {
  if (!vehicleParams) return null;

  if (typeof vehicleParams === 'string') {
    try {
      return JSON.parse(vehicleParams);
    } catch {
      return null;
    }
  }

  return vehicleParams as Record<string, any>;
};
